import { datadogRum } from '@datadog/browser-rum';
import { useMatches } from '@ksyos/router';
import { kebabCase } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isUuid } from '@common/utils/uuid';
import type { IReduxState } from '../../configureStore';

let lastViewName: string = '';

/**
 * useDatadogView automatically registers the page view based on the url and routes.
 * This is called from the App bootstrap.
 */
export function useDatadogView() {
    const matchedRoutes = useMatches();
    const matchedRoute = [...matchedRoutes].pop();
    const { loggedIn } = useSelector((state: IReduxState) => state.authentication);

    useEffect(() => {
        if (matchedRoute?.pathname) {
            startView(matchedRoute.pathname);
        }
    }, [matchedRoute?.pathname, loggedIn]);
}

/**
 * startView registers a new pageview in Datadog.
 * This should be called when a page is opened within a component when this is _not_ represented by a url/route change.
 */
export function startView(viewName: string) {
    if (viewName !== lastViewName) {
        const cleanViewName = cleanupViewName(viewName);
        datadogRum.startView({ name: cleanViewName });
        lastViewName = viewName;
    }
}

/**
 * setViewName changes the name of the current view without starting a new one.
 * This should be called when the the url does not properly represent the page that is open.
 */
export function setViewName(viewName: string) {
    if (viewName !== lastViewName) {
        const cleanViewName = cleanupViewName(viewName);
        datadogRum.setViewName(cleanViewName);
        lastViewName = viewName;
    }
}

function cleanupViewName(viewName: string) {
    const parts = viewName.split('/');

    return parts.map((part) => (isUuid(part) ? '?' : kebabCase(part))).join('/');
}
